import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CommonFormLayout from "../EmsOptimized/Form/CommonFormLayout";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import axios from "axios";
import { ApplicationRoles, getOrganizationUser } from "../../Apis/Organization-User";
import { MAIN_URL } from "../../Configurations/Urls";
import { fetchUserTypes } from "../../Apis/UserTypes";
import SelectWithImage from "../../Components/Inputs/SelectWithImage";
import { fetchGeneralCountries } from "../../Apis/OrganizationLocation";
import { fetchOrganizationEmployee } from "../../Apis/Employee-api";
import { fetchInterns } from "../../Apis/InternManagement";
import {
  noAutofillEmailInputProps,
  noAutofillPasswordInputProps,
} from "../../Utils/signupFormAutofill";

const getOrganizationUserTypeName = (user) =>
  user?.user_types?.user_type_name ??
  user?.UserTypes?.user_type_name ??
  user?.user_type ??
  user?.user_type_name ??
  "";

const isSharedResourceOrganizationUser = (user, sharedTypeIds = new Set()) => {
  const typeId = Number(user?.organization_user_type_id);
  if (Number.isFinite(typeId) && sharedTypeIds.has(typeId)) {
    return true;
  }
  const name = String(getOrganizationUserTypeName(user)).trim().toLowerCase();
  return name === "shared resource" || name === "shared resources";
};

const excludeSharedResourceUsers = (users, userTypes = []) => {
  const sharedTypeIds = new Set(
    userTypes
      .filter((type) => {
        const name = String(type?.user_type_name || "").trim().toLowerCase();
        return name === "shared resource" || name === "shared resources";
      })
      .map((type) => Number(type.organization_user_type_id))
      .filter((id) => Number.isFinite(id))
  );

  return (Array.isArray(users) ? users : []).filter(
    (user) => !isSharedResourceOrganizationUser(user, sharedTypeIds)
  );
};

const cleanContactValue = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "-") return "";
  return text;
};

const getStudentDisplayName = (student) =>
  [student?.first_name, student?.middle_name, student?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  String(student?.student_id || "").trim();

const getStudentContact = (student) => {
  if (!student) return { email: "", phone: "" };
  return {
    email: cleanContactValue(student.email),
    phone:
      cleanContactValue(student.phone) ||
      cleanContactValue(student.alternate_phone),
  };
};

const extractStudentsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.students)) return payload.students;
  return [];
};

const getPersonWorkContact = (person) => {
  if (!person) return { email: "", phone: "" };

  const contactRaw = person.contact;
  const contact = Array.isArray(contactRaw)
    ? contactRaw[0]
    : contactRaw && typeof contactRaw === "object"
      ? contactRaw
      : null;

  const workEmail =
    cleanContactValue(person.work_email) ||
    cleanContactValue(contact?.work_email);
  const workPhone =
    cleanContactValue(person.work_phone_number) ||
    cleanContactValue(contact?.work_phone_number);

  const personalEmail =
    cleanContactValue(person.personal_email) ||
    cleanContactValue(contact?.personal_email) ||
    cleanContactValue(person.email);
  const personalPhone =
    cleanContactValue(person.personal_phone_number) ||
    cleanContactValue(contact?.personal_phone_number) ||
    cleanContactValue(person.phone);

  return {
    email: workEmail || personalEmail,
    phone: workPhone || personalPhone,
  };
};

function UsersAdd() {
  const [countryCode, setCountryCode] = useState(null);
  const { userData } = useAuthStore();
  const [Roles, setRoles] = useState([]);
  const org = userData?.organization;
  const [userTypes, setUserTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [sharedSourceOrganizationId, setSharedSourceOrganizationId] = useState("");
  const [Employees, setEmployees] = useState([]);
  const [Interns, setInterns] = useState([]);
  const [Students, setStudents] = useState([]);

  useEffect(() => {
    fetchGeneralCountries()
      .then((data) => {
        const formatted = data.countries.map((item) => ({
          name: item.country_name,
          code: item.country_code,
          phone_code: item.country_phone_code,
        }));
        setCountries(formatted);
        setCountryCode(formatted.find((item) => item.code === "IN"));
      })
      .catch((err) => setFormErrors({ general: err.message }));
  }, []);

  const [userDetails, setUserDetails] = useState({
    user_name: "",
    phone: "",
    application_user_role_id: null,
    organization_user_role_id: "",
    organization_user_role_ids: [],
    password_hash: "",
    organization_user_type_id: "",
    phone_code: "",
    organization_id: org?.organization_id,
    email: "",
    organization_person_id: "",
    organization_ems_student_id: "",
    application_user_id: "",
    is_active: 1,
  });

  useEffect(() => {
    if (countryCode?.phone_code) {
      setUserDetails((prev) => ({
        ...prev,
        phone_code: countryCode.phone_code,
      }));
    }
  }, [countryCode]);

  useEffect(() => {
    if (!org?.organization_id) return;
    fetchOrganizationEmployee(org.organization_id)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(
          list.filter((item) => item.employment_status !== "Exited")
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, general: err.message }));
      });
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id) return;
    fetchInterns(org.organization_id)
      .then((data) => {
        const interns = data?.intership?.data || data?.interns?.data || [];
        setInterns(
          interns.filter(
            (intern) =>
              intern?.status?.internship_status_name !== "Exited"
          )
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, general: err.message }));
      });
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id) return;
    axios
      .get(`${MAIN_URL}/api/ems/students`, {
        params: {
          organization_id: org.organization_id,
          per_page: "all",
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        const list = extractStudentsList(response.data?.data);
        setStudents(
          list.filter((item) => {
            if (item?.organization_user_id) return false;
            const status = String(item?.student_status || "").toLowerCase();
            return status !== "dropped";
          })
        );
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, general: err.message }));
      });
  }, [org?.organization_id]);

  useEffect(() => {
    fetchUserTypes(org?.organization_id)
      .then((data) => {
        setUserTypes(data?.userTypes?.data);
      })
      .catch((err) => {
        setFormErrors({ general: err.message });
      });
  }, [org?.organization_id]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) {
      return;
    }
    ApplicationRoles(org.organization_id)
      .then((data) => {
        setRoles(data?.userroles);
      })
      .catch((err) => {
        setFormErrors({ general: err.message });
      });
  }, [org?.organization_id]);

  const selectedUserType = userTypes?.find(
    (type) =>
      type.organization_user_type_id === userDetails.organization_user_type_id
  );

  const isEmployeeUser =
    selectedUserType?.user_type_name?.toLowerCase() === "employee";
  const isInternUser =
    selectedUserType?.user_type_name?.toLowerCase() === "intern";
  const isStudentUser =
    selectedUserType?.user_type_name?.toLowerCase() === "student";
  const isLinkedRecordUser = isEmployeeUser || isInternUser || isStudentUser;
  const isSharedResourceUser =
    selectedUserType?.user_type_name?.toLowerCase() === "shared resource";
  const sharedMaskedPassword =
    isSharedResourceUser && userDetails.application_user_id ? "********" : "";

  useEffect(() => {
    const byId = new Map();

    (userData?.metadata || [])
      .map((item) => item?.organization)
      .filter((item) => item?.organization_id)
      .forEach((item) => {
        byId.set(Number(item.organization_id), item);
      });

    const allKnownOrganizations = [
      ...(Array.isArray(userData?.organizations) ? userData.organizations : []),
      ...(Array.isArray(userData?.organization) ? userData.organization : []),
      ...(userData?.organization ? [userData.organization] : []),
    ].filter((item) => item?.organization_id);

    const knownById = new Map(
      allKnownOrganizations.map((item) => [Number(item.organization_id), item])
    );

    (userData?.metadata || [])
      .map((item) => Number(item?.organization_id))
      .filter((id) => Number.isFinite(id))
      .forEach((id) => {
        if (!byId.has(id)) {
          byId.set(id, knownById.get(id) || { organization_id: id });
        }
      });

    if (byId.size === 0 && userData?.organization?.organization_id) {
      byId.set(Number(userData.organization.organization_id), userData.organization);
    }

    const currentOrgId = Number(
      org?.organization_id ?? userData?.organization?.organization_id
    );
    const allOrgs = Array.from(byId.values());
    setOrganizations(
      Number.isFinite(currentOrgId)
        ? allOrgs.filter((item) => Number(item.organization_id) !== currentOrgId)
        : allOrgs
    );
  }, [userData, org?.organization_id]);

  useEffect(() => {
    if (!sharedSourceOrganizationId) return;
    const stillValid = organizations.some(
      (item) => String(item.organization_id) === String(sharedSourceOrganizationId)
    );
    if (!stillValid) {
      setSharedSourceOrganizationId("");
      setOrganizationUsers([]);
    }
  }, [organizations, sharedSourceOrganizationId]);

  useEffect(() => {
    if (!isSharedResourceUser || !sharedSourceOrganizationId) {
      setOrganizationUsers([]);
      return;
    }
    getOrganizationUser(sharedSourceOrganizationId)
      .then((data) => {
        const rawUsers = Array.isArray(data?.users)
          ? data.users
          : data?.users?.data || [];
        setOrganizationUsers(excludeSharedResourceUsers(rawUsers, userTypes));
      })
      .catch((err) => {
        setFormErrors((prev) => ({ ...prev, application_user_id: err.message }));
      });
  }, [isSharedResourceUser, sharedSourceOrganizationId, userTypes]);

  useEffect(() => {
    if (!userDetails.application_user_id || organizationUsers.length === 0) return;
    const stillValid = organizationUsers.some(
      (item) =>
        String(item?.application_user_id) === String(userDetails.application_user_id)
    );
    if (!stillValid) {
      setUserDetails((prev) => ({
        ...prev,
        application_user_id: "",
        user_name: "",
        phone: "",
        email: "",
      }));
    }
  }, [organizationUsers, userDetails.application_user_id]);

  const handleDetailChange = (e) => {
    const { name, value } = e.target;

    if (name === "organization_person_id") {
      const personId = Number(value);
      const selectedEmp = Employees.find(
        (emp) => Number(emp.organization_person_id) === personId
      );
      if (selectedEmp) {
        const { email: workEmail, phone: workPhone } =
          getPersonWorkContact(selectedEmp);
        setUserDetails((prev) => ({
          ...prev,
          organization_person_id: value,
          user_name:
            selectedEmp?.name ||
            `${selectedEmp?.first_name || ""} ${selectedEmp?.last_name || ""}`.trim(),
          phone: workPhone,
          email: workEmail,
        }));
        return;
      }

      const selectedIntern = Interns.find(
        (i) => Number(i.organization_person_id) === personId
      );
      if (selectedIntern) {
        const { email: workEmail, phone: workPhone } =
          getPersonWorkContact(selectedIntern);
        setUserDetails((prev) => ({
          ...prev,
          organization_person_id: value,
          user_name:
            selectedIntern?.org_persons?.person?.full_name?.trim() ||
            selectedIntern?.name ||
            "",
          phone: workPhone,
          email: workEmail,
        }));
      }
    } else if (name === "organization_ems_student_id") {
      const selectedStudent = Students.find(
        (student) =>
          Number(student.organization_ems_student_id) === Number(value)
      );
      if (selectedStudent) {
        const { email, phone } = getStudentContact(selectedStudent);
        const countryPhoneCode = selectedStudent?.country?.country_phone_code;
        if (countryPhoneCode) {
          const matchedCountry = countries.find(
            (item) => String(item.phone_code) === String(countryPhoneCode)
          );
          if (matchedCountry) {
            setCountryCode(matchedCountry);
          }
        }
        setUserDetails((prev) => ({
          ...prev,
          organization_ems_student_id: value,
          user_name: getStudentDisplayName(selectedStudent),
          phone,
          email,
        }));
        return;
      }
      setUserDetails((prev) => ({
        ...prev,
        organization_ems_student_id: "",
        user_name: "",
        phone: "",
        email: "",
      }));
    } else if (name === "organization_user_type_id") {
      const selectedType = userTypes.find(
        (type) => type.organization_user_type_id === parseInt(value, 10)
      );
      const isEmployee =
        selectedType?.user_type_name?.toLowerCase() === "employee";
      const isIntern =
        selectedType?.user_type_name?.toLowerCase() === "intern";
      const isStudent =
        selectedType?.user_type_name?.toLowerCase() === "student";
      const isShared =
        selectedType?.user_type_name?.toLowerCase() === "shared resource";
      const linksRecord = isEmployee || isIntern || isStudent;

      setUserDetails((prev) => ({
        ...prev,
        organization_user_type_id: value,
        organization_id: org?.organization_id,
        organization_person_id: isEmployee || isIntern ? prev.organization_person_id : "",
        organization_ems_student_id: isStudent
          ? prev.organization_ems_student_id
          : "",
        application_user_id: isShared ? prev.application_user_id : "",
        user_name: linksRecord ? prev.user_name : "",
        phone: linksRecord ? prev.phone : "",
        email: linksRecord ? prev.email : "",
        password_hash: isShared ? "" : prev.password_hash,
      }));
      if (!isShared) {
        setSharedSourceOrganizationId("");
        setOrganizationUsers([]);
      }
    } else if (name === "organization_id") {
      setSharedSourceOrganizationId(value);
      setUserDetails((prev) => ({
        ...prev,
        application_user_id: "",
        user_name: "",
        phone: "",
        email: "",
      }));
    } else if (name === "application_user_id") {
      const selectedOrgUser = organizationUsers.find(
        (item) => String(item?.application_user_id) === String(value)
      );
      const appUser = selectedOrgUser?.applicationuser?.[0];
      setUserDetails((prev) => ({
        ...prev,
        application_user_id: value,
        user_name: appUser?.full_name || "",
        phone: appUser?.phone_number || "",
        email: appUser?.email || "",
        password_hash: value ? "********" : "",
      }));
    } else if (name === "organization_user_role_ids") {
      const selected = Array.isArray(value) ? value : [];
      const first = selected[0];
      setUserDetails((prev) => ({
        ...prev,
        organization_user_role_ids: selected.map((role) =>
          String(role.organization_user_role_id)
        ),
        organization_user_role_id: first
          ? String(first.organization_user_role_id)
          : "",
        application_user_role_id:
          first?.application_user_role_id != null
            ? Number(first.application_user_role_id)
            : null,
      }));
      setFormErrors((prev) => ({ ...prev, organization_user_role_id: "" }));
    } else {
      setUserDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  useEffect(() => {
    if (isSharedResourceUser) {
      setExistingClientUser(null);
      return undefined;
    }

    const email = String(userDetails.email || "").trim();
    const phone = String(userDetails.phone || "").trim();
    if (!org?.organization_id || phone.length < 8) {
      setExistingClientUser(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/user/match-existing-user`,
          {
            phone,
            ...(isValidEmail(email) ? { email } : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (cancelled) return;
        const status = response.data?.status;
        if (status === "same_client" || status === "already_in_organization") {
          const matched = { ...(response.data.user || { full_name: "" }), match_status: status };
          setExistingClientUser(matched);
          setUserDetails((prev) => {
            const nextEmail = matched.email || prev.email;
            const nextName = matched.full_name || prev.user_name;
            if (prev.email === nextEmail && prev.user_name === nextName) return prev;
            return { ...prev, user_name: nextName, email: nextEmail };
          });
          setFormErrors((prev) => ({
            ...prev,
            email: "",
            phone: "",
            password_hash: "",
          }));
          return;
        }
        setExistingClientUser(null);
        if (status === "new") {
          setFormErrors((prev) => ({ ...prev, email: "", phone: "" }));
          return;
        }
        if (status && response.data?.message) {
          const errorField = response.data.field === "phone" || status === "phone_taken" ? "phone" : "email";
          setFormErrors((prev) => ({
            ...prev,
            email: errorField === "email" ? response.data.message : "",
            phone: errorField === "phone" ? response.data.message : "",
          }));
        }
      } catch (error) {
        if (!cancelled) setExistingClientUser(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userDetails.email, userDetails.phone, isSharedResourceUser, org?.organization_id]);

  const [formErrors, setFormErrors] = useState({});
  const [existingClientUser, setExistingClientUser] = useState(null);

  const validateForm = () => {
    const errors = {};

    if (!userDetails.user_name) errors.user_name = "User's Name is required.";
    if (!userDetails.phone) errors.phone = "Phone is required.";
    if (!userDetails.organization_user_type_id)
      errors.organization_user_type_id = "User Type is required.";
    if (
      !(userDetails.organization_user_role_ids || []).length &&
      !userDetails.organization_user_role_id
    )
      errors.organization_user_role_id = "Role is required.";

    if ((isEmployeeUser || isInternUser) && !userDetails.organization_person_id) {
      errors.organization_person_id = isEmployeeUser
        ? "Employee is required."
        : "Intern is required.";
    }

    if (isStudentUser && !userDetails.organization_ems_student_id) {
      errors.organization_ems_student_id = "Student is required.";
    }

    if (isSharedResourceUser && !sharedSourceOrganizationId) {
      errors.organization_id = "Organization is required.";
    }
    if (isSharedResourceUser && !userDetails.application_user_id) {
      errors.application_user_id = "User is required.";
    }
    if (userDetails.email && !isValidEmail(userDetails.email)) {
      errors.email = "Enter a valid email address.";
    }
    if (!userDetails.email) errors.email = "Email is required.";
    if (!isSharedResourceUser && !existingClientUser && !userDetails.password_hash) {
      errors.password_hash = "Password is required.";
    }
    if (!isSharedResourceUser && formErrors.email && !existingClientUser) {
      errors.email = formErrors.email;
    }
    if (
      !isSharedResourceUser &&
      formErrors.phone &&
      formErrors.phone !== "Phone is required." &&
      !existingClientUser
    ) {
      errors.phone = formErrors.phone;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFinalSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    try {
      const targetOrgId = org?.organization_id;
      const endpoint = isSharedResourceUser
        ? `${MAIN_URL}/api/organizations/${targetOrgId}/user/create-from-existing-user`
        : `${MAIN_URL}/api/organizations/${targetOrgId}/user/create-new-user`;

      const payload = isSharedResourceUser
        ? {
            source_organization_id: Number(sharedSourceOrganizationId),
            application_user_id: Number(userDetails.application_user_id),
            organization_user_type_id: Number(userDetails.organization_user_type_id),
            organization_user_role_ids: (userDetails.organization_user_role_ids || [])
              .map((id) => Number(id))
              .filter((id) => id > 0),
            organization_user_role_id: userDetails.organization_user_role_id
              ? Number(userDetails.organization_user_role_id)
              : null,
            application_user_role_id: userDetails.application_user_role_id
              ? Number(userDetails.application_user_role_id)
              : null,
            organization_person_id: userDetails.organization_person_id
              ? Number(userDetails.organization_person_id)
              : null,
          }
        : {
            ...userDetails,
            organization_user_role_ids: (userDetails.organization_user_role_ids || [])
              .map((id) => Number(id))
              .filter((id) => id > 0),
            organization_user_role_id: userDetails.organization_user_role_id
              ? Number(userDetails.organization_user_role_id)
              : null,
            application_user_role_id: userDetails.application_user_role_id
              ? Number(userDetails.application_user_role_id)
              : null,
            organization_ems_student_id: userDetails.organization_ems_student_id
              ? Number(userDetails.organization_ems_student_id)
              : null,
            phone_verified: 0,
            is_active: userDetails.is_active ? 1 : 0,
          };

      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success(
        existingClientUser
          ? "Existing user added to this organization."
          : "User created successfully!"
      );
      setFormErrors({});
      navigate("/users");
    } catch (err) {
      console.error(err);

      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);

        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");

        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CommonFormLayout
      mode="add"
      loading={false}
      btnLoading={btnLoading}
      headerProps={{
        addMessage: "User",
        updateMessage: "User",
        homeLink: "/users",
        homeText: "Users",
      }}
      onSubmit={handleFinalSubmit}
    >
      <Grid container spacing={2}>
          <TextField
            select
            fullWidth
            label="User Type"
            name="organization_user_type_id"
            value={userDetails.organization_user_type_id}
            onChange={handleDetailChange}
            error={!!formErrors.organization_user_type_id}
            helperText={formErrors.organization_user_type_id}
            disabled={userTypes?.length === 0}
            required
            sx={{ textTransform: "capitalize" }}
          >
            {userTypes?.map((option) => (
              <MenuItem
                key={option?.organization_user_type_id}
                value={option?.organization_user_type_id}
                sx={{ textTransform: "capitalize" }}
              >
                {option?.user_type_name}
              </MenuItem>
            ))}
          </TextField>

          {isSharedResourceUser && (
            <TextField
              select
              fullWidth
              label="Organization"
              name="organization_id"
              value={sharedSourceOrganizationId}
              onChange={handleDetailChange}
              error={!!formErrors.organization_id}
              helperText={formErrors.organization_id}
              required
            >
              {organizations?.map((option) => (
                <MenuItem key={option.organization_id} value={option.organization_id}>
                  {option.organization_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isSharedResourceUser && (
            <TextField
              select
              fullWidth
              label="Organization User"
              name="application_user_id"
              value={userDetails.application_user_id}
              onChange={handleDetailChange}
              error={!!formErrors.application_user_id}
              helperText={formErrors.application_user_id}
              required
            >
              {organizationUsers?.map((option) => (
                <MenuItem
                  key={option?.organization_user_id}
                  value={option?.application_user_id}
                >
                  {option?.applicationuser?.[0]?.full_name || "Unknown User"}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isEmployeeUser && (
            <Autocomplete
              fullWidth
              options={Employees || []}
              getOptionLabel={(option) =>
                `${option?.name || ""} - ID: ${option?.employee_code || ""}`.trim()
              }
              value={
                Employees?.find(
                  (emp) =>
                    String(emp?.organization_person_id) ===
                    String(userDetails?.organization_person_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDetailChange({
                  target: {
                    name: "organization_person_id",
                    value: newValue?.organization_person_id || "",
                  },
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option?.organization_person_id ===
                value?.organization_person_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  name="organization_person_id"
                  required
                  error={!!formErrors?.organization_person_id}
                  helperText={formErrors?.organization_person_id}
                  fullWidth
                />
              )}
            />
          )}

          {isInternUser && (
            <Autocomplete
              fullWidth
              options={Interns || []}
              getOptionLabel={(option) =>
                `${option?.org_persons?.person?.full_name || ""} (${option?.org_persons?.person_code || ""})`.trim()
              }
              value={
                Interns?.find(
                  (i) =>
                    String(i?.organization_person_id) ===
                    String(userDetails?.organization_person_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDetailChange({
                  target: {
                    name: "organization_person_id",
                    value: newValue?.organization_person_id || "",
                  },
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option?.organization_person_id ===
                value?.organization_person_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Intern"
                  name="organization_person_id"
                  required
                  error={!!formErrors?.organization_person_id}
                  helperText={formErrors?.organization_person_id}
                  fullWidth
                />
              )}
            />
          )}

          {isStudentUser && (
            <Autocomplete
              fullWidth
              options={Students || []}
              getOptionLabel={(option) => {
                const name = getStudentDisplayName(option);
                const code = option?.student_id ? String(option.student_id) : "";
                if (name && code) return `${name} (${code})`;
                return name || code || `Student #${option?.organization_ems_student_id || ""}`;
              }}
              value={
                Students?.find(
                  (student) =>
                    String(student?.organization_ems_student_id) ===
                    String(userDetails?.organization_ems_student_id)
                ) || null
              }
              onChange={(event, newValue) => {
                handleDetailChange({
                  target: {
                    name: "organization_ems_student_id",
                    value: newValue?.organization_ems_student_id || "",
                  },
                });
              }}
              isOptionEqualToValue={(option, value) =>
                Number(option?.organization_ems_student_id) ===
                Number(value?.organization_ems_student_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Student"
                  name="organization_ems_student_id"
                  required
                  error={!!formErrors?.organization_ems_student_id}
                  helperText={formErrors?.organization_ems_student_id}
                  fullWidth
                />
              )}
            />
          )}

          <Autocomplete
            multiple
            fullWidth
            options={Roles || []}
            getOptionLabel={(option) =>
              option.user_role_name || option.role_name || ""
            }
            isOptionEqualToValue={(option, value) =>
              String(option?.organization_user_role_id) ===
              String(value?.organization_user_role_id)
            }
            value={(Roles || []).filter((role) =>
              (userDetails.organization_user_role_ids || []).includes(
                String(role.organization_user_role_id)
              )
            )}
            onChange={(event, selected) =>
              handleDetailChange({
                target: {
                  name: "organization_user_role_ids",
                  value: selected,
                },
              })
            }
            disabled={!Roles?.length}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Roles"
                name="organization_user_role_id"
                required
                error={!!formErrors.organization_user_role_id}
                helperText={
                  formErrors.organization_user_role_id ||
                  "A person can have Staff and Trainer roles at the same time."
                }
              />
            )}
          />

          <TextField
            value={userDetails.user_name}
            error={!!formErrors.user_name}
            helperText={formErrors.user_name}
            fullWidth
            label="Full Name"
            name="user_name"
            onChange={handleDetailChange}
            required
            disabled={isLinkedRecordUser || !!existingClientUser}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <div style={{ width: "25%" }}>
              <div
                style={{
                  border: "2px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: "6px",
                }}
              >
                <SelectWithImage
                  value={countryCode}
                  error={false}
                  options={countries}
                  optionkey="phone_code"
                  onChange={(e, val) => setCountryCode(val)}
                  width="100%"
                  minWidth="20px"
                  disabled={false}
                  searchKeys={["name", "phone_code"]}
                />
              </div>
            </div>

            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={userDetails.phone === "-" ? "" : userDetails.phone}
              error={!!formErrors.phone}
              helperText={
                formErrors.phone ||
                (existingClientUser
                  ? existingClientUser.match_status === "already_in_organization"
                    ? "This phone number already belongs to a user in this organization. They will be linked here."
                    : "This phone number already belongs to a user of this client. They will be added here with the role you select."
                  : isLinkedRecordUser
                    ? "You can edit the phone number before creating the user."
                    : "")
              }
              onChange={handleDetailChange}
              required
              inputProps={{
                maxLength: 20,
                inputMode: "numeric",
                onInput: (e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                },
              }}
            />
          </div>

          <TextField
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={userDetails.email === "-" ? "" : userDetails.email}
            error={!!formErrors.email}
            helperText={
              formErrors.email ||
              (existingClientUser
                ? "This user already belongs to another organization of this client. They will be added here with the role you select."
                : isLinkedRecordUser
                  ? "You can edit the email before creating the user."
                  : "")
            }
            onChange={handleDetailChange}
            required
            disabled={!!existingClientUser}
            autoComplete="off"
            inputProps={noAutofillEmailInputProps}
          />

          <TextField
            fullWidth
            label="Password"
            name="password_hash"
            type="password"
            value={
              isSharedResourceUser || existingClientUser
                ? sharedMaskedPassword || "********"
                : userDetails.password_hash
            }
            error={!!formErrors.password_hash}
            helperText={
              isSharedResourceUser
                ? "Password is encrypted for organization user and cannot be viewed."
                : existingClientUser
                  ? "This user already has a login. A new password is not required."
                : formErrors.password_hash ||
                  (isLinkedRecordUser
                    ? "Set the login password for this user."
                    : "")
            }
            onChange={handleDetailChange}
            required={!isSharedResourceUser && !existingClientUser}
            disabled={isSharedResourceUser || !!existingClientUser}
            autoComplete="new-password"
            inputProps={noAutofillPasswordInputProps}
          />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(userDetails.is_active)}
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    is_active: e.target.checked ? 1 : 0,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#0891B2" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#0891B2",
                  },
                }}
              />
            }
            label={userDetails.is_active ? "Active" : "Inactive"}
          />
        </Grid>
    </CommonFormLayout>
  );
}

export default UsersAdd;
